// hooks/useBrandFilters.js
import { useState, useEffect, useMemo } from 'react';

const useBrandFilters = (allPromotions = [], onFilterChange) => {
  const [filters, setFilters] = useState({
    brand: null, // Will be set to first brand when brands are loaded
    searchQuery: ''
  });

  // Extract unique brands from promotions and sort like Home page
  const brands = useMemo(() => {
    if (!allPromotions.length) return [];
    
    // Create a map to store brand info for sorting
    const brandMap = new Map();
    
    allPromotions.forEach(promotion => {
      const brandName = promotion.brandName || promotion.brand;
      if (brandName && !brandMap.has(brandName)) {
        // Store brand info from the first promotion we see for this brand
        const brandInfo = {
          name: brandName,
          rating: parseFloat(promotion.brandRating) || 0,
          createdAt: promotion.brandCreatedAt || promotion.createdAt
        };
        brandMap.set(brandName, brandInfo);
        
        // Debug logging
        console.log('Brand extracted for sorting:', brandInfo);
      }
    });
    
    // Convert to array and sort like Home page (by rating, then by creation date)
    const brandArray = Array.from(brandMap.values());
    brandArray.sort((a, b) => {
      // First sort by rating (highest first)
      if (b.rating !== a.rating) {
        return b.rating - a.rating;
      }
      // Then by creation date (newest first)
      if (a.createdAt && b.createdAt) {
        const aDate = a.createdAt.toDate ? a.createdAt.toDate() : new Date(a.createdAt);
        const bDate = b.createdAt.toDate ? b.createdAt.toDate() : new Date(b.createdAt);
        return bDate - aDate;
      }
      // Fallback to alphabetical if no dates
      return a.name.localeCompare(b.name);
    });
    
    console.log('Brands sorted:', brandArray.map(b => `${b.name} (${b.rating})`));
    
    // Return just the brand names in sorted order
    return brandArray.map(brand => brand.name);
  }, [allPromotions]);

  // Set first brand as default when brands are loaded
  useEffect(() => {
    if (brands.length > 0 && !filters.brand) {
      setFilters(prev => ({
        ...prev,
        brand: brands[0]
      }));
    }
  }, [brands, filters.brand]);

  // Filtered promotions based on current filters
  const filteredPromotions = useMemo(() => {
    if (!allPromotions.length) return [];
    
    let filtered = allPromotions;

    // Filter by brand
    if (filters.brand) {
      filtered = filtered.filter(promotion => 
        (promotion.brandName || promotion.brand) === filters.brand
      );
    }

    // Filter by search query
    if (filters.searchQuery.trim()) {
      const query = filters.searchQuery.toLowerCase().trim();
      filtered = filtered.filter(promotion => {
        const title = (promotion.title || '').toLowerCase();
        const description = (promotion.description || '').toLowerCase();
        const brand = (promotion.brandName || promotion.brand || '').toLowerCase();
        
        return title.includes(query) || 
               description.includes(query) || 
               brand.includes(query);
      });
    }

    return filtered;
  }, [allPromotions, filters]);

  // Update brand filter
  const updateBrand = (brand) => {
    setFilters(prev => ({
      ...prev,
      brand
    }));
  };

  // Update search query filter
  const updateSearchQuery = (query) => {
    setFilters(prev => ({
      ...prev,
      searchQuery: query
    }));
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      brand: brands.length > 0 ? brands[0] : null,
      searchQuery: ''
    });
  };

  // Reset filters to default
  const resetFilters = () => {
    setFilters({
      brand: brands.length > 0 ? brands[0] : null,
      searchQuery: ''
    });
  };

  // Check if any filters are active (search only, since brand is always selected)
  const hasActiveFilters = useMemo(() => {
    return filters.searchQuery !== '';
  }, [filters]);

  // Notify parent component when filters change
  useEffect(() => {
    if (onFilterChange) {
      onFilterChange(filteredPromotions, filters);
    }
  }, [filteredPromotions, filters, onFilterChange]);

  return {
    // Current filters
    filters,
    
    // Available options
    brands,
    
    // Filtered data
    filteredPromotions,
    
    // Filter actions
    updateBrand,
    updateSearchQuery,
    clearFilters,
    resetFilters,
    
    // Computed values
    hasActiveFilters,
    filteredCount: filteredPromotions.length,
    totalCount: allPromotions.length
  };
};

export default useBrandFilters;